import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import SortableTh, { sortRows } from '../components/SortableTh'
import Paginacao, { usePaginacao } from '../components/Paginacao'
import SelecaoMultipla from '../components/SelecaoMultipla'
import { useConfig } from '../contexts/ConfigContext'
import { corClara } from '../utils/cor'
import { escapeHtml } from '../utils/html'
import { avisar } from '../components/Avisos'

// Negociacoes perdidas no CRM em que se respondeu "Sim" pra "retornar contato no proximo ano"
// (pedido do usuario, 26/10) — nos moldes da Renovacoes: lista abre vazia, busca + filtro salvo,
// Obs inline, Gerar PDF e "Gerar Card" (nova negociacao no CRM ja preenchida).

const MOTIVOS_PERDA = ['Preço muito alto', 'Fechou com outra corretora', 'Fechou no Banco', 'Vendeu o veículo', 'Sem retorno do cliente', 'Desistiu do seguro', 'Outro']
const RAMOS = ['Acidentes Pessoais', 'Automóvel', 'Bike', 'Carta Verde', 'Condomínio', 'Empresarial', 'Equipamentos', 'Residencial', 'Responsabilidade Civil', 'Vida em Grupo', 'Vida Individual', 'Viagem']
const STATUS_RETORNO = {
  aguardando: { label: 'Aguardando', cls: 'badge-blue' },
  retornar: { label: 'Retornar', cls: 'badge-amber' },
  card_gerado: { label: 'Card Gerado', cls: 'badge-green' },
}
const CHAVE_FILTROS = 'perdidos.filtros'
const FILTROS_VAZIOS = { ramos: [], motivos: [], status: [], retornoDe: '', retornoAte: '' }
const lerFiltros = () => {
  try { return JSON.parse(localStorage.getItem(CHAVE_FILTROS)) || {} } catch { return {} }
}
const temCriterio = (f) => !!(f.ramos.length || f.motivos.length || f.status.length || f.retornoDe || f.retornoAte)

const dia = (d) => (d ? String(d).slice(0, 10) : '')
const fmtData = (d) => (d ? dia(d).split('-').reverse().join('/') : '—')
const diasAte = (d) => Math.ceil((new Date(dia(d) + 'T00:00:00') - new Date(new Date().toDateString())) / 86400000)
// Card gerado > data do retorno ja chegou (Retornar) > ainda nao chegou (Aguardando)
const statusRetorno = (c) => c.retorno_funil_id ? 'card_gerado' : (c.data_retorno && diasAte(c.data_retorno) <= 0 ? 'retornar' : 'aguardando')

export default function Perdidos() {
  const { config } = useConfig()
  const navigate = useNavigate()
  const [cards, setCards] = useState([])
  const [carregando, setCarregando] = useState(true)
  const [busca, setBusca] = useState('')
  const [obsEdits, setObsEdits] = useState({})
  const [usarFiltro, setUsarFiltro] = useState(() => !!lerFiltros().usarFiltro)
  const [filtros, setFiltros] = useState(() => {
    const salvos = lerFiltros().filtros || {}
    return Object.fromEntries(Object.entries(FILTROS_VAZIOS).map(([k, v]) => [k, salvos[k] ?? v]))
  })
  const setFiltro = (campo, valor) => setFiltros(f => ({ ...f, [campo]: valor }))
  useEffect(() => {
    try { localStorage.setItem(CHAVE_FILTROS, JSON.stringify({ usarFiltro, filtros })) } catch { /* sem localStorage: so nao persiste */ }
  }, [usarFiltro, filtros])

  const [sortCol, setSortCol] = useState('retorno')
  const [sortDir, setSortDir] = useState('asc')
  const toggleSort = (key) => {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(key); setSortDir('asc') }
  }

  useEffect(() => {
    // Volta do CRM: restaura a busca digitada
    const salva = sessionStorage.getItem('perdidosBuscaVoltar')
    if (salva !== null) { sessionStorage.removeItem('perdidosBuscaVoltar'); setBusca(salva) }
    api.get('/funil')
      .then(r => setCards((Array.isArray(r.data) ? r.data : []).filter(c => c.etapa === 'fechamento' && c.retornar_contato)))
      .catch(() => setCards([]))
      .finally(() => setCarregando(false))
  }, [])

  const salvarObs = async (id, anterior, valor) => {
    if ((anterior || '') === valor) return
    try {
      await api.patch(`/funil/${id}/obs-retorno`, { obs_retorno: valor })
      setCards(prev => prev.map(c => c.id === id ? { ...c, obs_retorno: valor } : c))
    } catch (err) {
      avisar('Erro ao salvar observação: ' + (err.response?.data?.erro || err.message))
    }
  }

  // Nova negociacao no CRM ja preenchida (cliente, ramo, tipo "perdido"); ao criar, o card perdido
  // fica marcado como "Card Gerado" (perdido_origem_id -> funil.retorno_funil_id).
  const gerarCard = (c) => {
    const eraRenovacao = ['Renovação Interna', 'Refeito', 'Renovação Perdida'].includes(c.tipo)
    sessionStorage.setItem('perdidosBuscaVoltar', busca)
    sessionStorage.setItem('crmFormPendente', JSON.stringify({
      cliente_id: c.cliente_id,
      etapa: 'prospeccao',
      ramo: c.ramo || 'Automóvel',
      tipo: eraRenovacao ? 'Renovação Perdida' : 'Negociação Perdido',
      observacoes: `Retorno de negociação perdida em ${fmtData(c.perdido_em)}. Motivo: ${c.motivo_perda || '—'}${c.obs_perda ? ` — ${c.obs_perda}` : ''}.`,
      apolice_origem_id: null,
      perdido_origem_id: c.id,
    }))
    navigate('/crm')
  }
  const verCard = (c) => {
    sessionStorage.setItem('perdidosBuscaVoltar', busca)
    sessionStorage.setItem('crmFormPendente', JSON.stringify({
      cliente_id: c.cliente_id, etapa: c.etapa, ramo: c.ramo || 'Automóvel', tipo: c.tipo || 'Novo',
      observacoes: c.observacoes || '', apolice_origem_id: c.apolice_origem_id || null, editandoId: c.id,
    }))
    navigate('/crm')
  }

  const colunas = [
    { key: 'retorno', label: 'Data do\nRetorno', get: c => c.data_retorno || '', date: true },
    { key: 'segurado', label: 'Segurado', get: c => c.nome || '' },
    { key: 'ramo', label: 'Ramo', get: c => c.ramo || '' },
    { key: 'tipo', label: 'Tipo', get: c => c.tipo || '' },
    { key: 'motivo', label: 'Motivo', get: c => c.motivo_perda || '' },
    { key: 'perdido', label: 'Perdido\nem', get: c => c.perdido_em || '', date: true },
    { key: 'status', label: 'Status', get: c => STATUS_RETORNO[statusRetorno(c)].label },
  ]

  const filtroAtivo = temCriterio(filtros)
  const semBusca = !filtroAtivo ? cards : cards.filter(c => {
    if (filtros.ramos.length && !filtros.ramos.includes(c.ramo || '')) return false
    if (filtros.motivos.length && !filtros.motivos.includes(c.motivo_perda || '')) return false
    if (filtros.status.length && !filtros.status.includes(statusRetorno(c))) return false
    const r = dia(c.data_retorno)
    if (filtros.retornoDe && (!r || r < filtros.retornoDe)) return false
    if (filtros.retornoAte && (!r || r > filtros.retornoAte)) return false
    return true
  })
  const q = busca.toLowerCase().trim()
  // Lista abre vazia (igual Renovacoes): so aparece algo depois de buscar ou filtrar
  const pesquisou = !!q || filtroAtivo
  const filtradas = !pesquisou ? [] : q ? semBusca.filter(c => c.nome?.toLowerCase().includes(q)) : semBusca
  const ordenadas = sortRows(filtradas, colunas, sortCol, sortDir)
  const paginacao = usePaginacao(ordenadas)

  const gerarPdf = (linhas) => {
    const cor = config.cor_primaria
    const hoje = new Date().toLocaleDateString('pt-BR')
    const td = (v, extra = '') => `<td style="padding:6px 8px;border-bottom:1px solid #eee;${extra}">${v}</td>`
    const corpo = linhas.map((c, i) => `<tr style="background:${i % 2 ? '#FAFAFA' : '#fff'};">
      ${td(fmtData(c.data_retorno), 'text-align:center;')}
      ${td(escapeHtml(c.nome), 'font-weight:600;')}
      ${td(escapeHtml(c.ramo) || '—', 'text-align:center;')}
      ${td(escapeHtml(c.motivo_perda) || '—', 'text-align:center;')}
      ${td(fmtData(c.perdido_em), 'text-align:center;')}
      ${td(STATUS_RETORNO[statusRetorno(c)].label, 'text-align:center;')}
      ${td(escapeHtml(c.obs_retorno || c.obs_perda) || '—')}
    </tr>`).join('')
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Perdidos - Retornar Contato</title>
<style>body{font-family:Arial,sans-serif;margin:0;color:#333;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.page{max-width:1100px;margin:0 auto;padding:20px}.hdr{background:${cor};color:#fff;padding:14px 20px;border-radius:6px;margin-bottom:6px;display:flex;justify-content:space-between}
.hdr h1{margin:0;font-size:16px}.sub{font-size:12px;color:#777;margin-bottom:14px}table{width:100%;border-collapse:collapse;font-size:11px}
th{text-align:left;padding:8px;background:${corClara(cor)};color:${cor};font-size:10px;text-transform:uppercase}th.c{text-align:center}
@media print{@page{size:A4 landscape;margin:10mm}.page{padding:0}}</style></head><body><div class="page">
<div class="hdr"><h1>PERDIDOS — RETORNAR CONTATO</h1><div style="font-size:11px">${hoje}</div></div>
<div class="sub">${linhas.length} ${linhas.length === 1 ? 'negociação' : 'negociações'}</div>
<table><thead><tr><th class="c">Data do Retorno</th><th>Segurado</th><th class="c">Ramo</th><th class="c">Motivo</th><th class="c">Perdido em</th><th class="c">Status</th><th>Obs</th></tr></thead>
<tbody>${corpo}</tbody></table></div><script>setTimeout(()=>window.print(),400)</script></body></html>`
    const win = window.open('', '_blank')
    if (win) { win.document.write(html); win.document.close() } else avisar('Permita pop-ups para gerar o PDF.')
  }

  return (
    <>
      <div style={{ padding: '32px 24px 0' }}>
        <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Perdidos</h2>
        <div style={{ fontSize: 13, color: '#888', marginTop: 4, marginBottom: 20 }}>Negociações perdidas no CRM em que ficou combinado retornar o contato no próximo ano.</div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
          <input className="form-input" placeholder="Buscar por nome do segurado..." value={busca} onChange={e => setBusca(e.target.value)}
            style={{ maxWidth: 420, width: '100%', border: '2px solid rgba(var(--primary-rgb),0.15)', borderRadius: 10 }} />
          <button type="button" className="btn btn-secondary" style={{ flexShrink: 0, whiteSpace: 'nowrap' }} onClick={() => setUsarFiltro(v => !v)}>
            {usarFiltro ? 'Fechar filtros' : 'Abrir filtros'}{temCriterio(filtros) && <span style={{ color: 'var(--primary)', marginLeft: 6 }}>●</span>}
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn btn-primary" style={{ flexShrink: 0 }} onClick={() => gerarPdf(ordenadas)} disabled={ordenadas.length === 0}>Gerar PDF</button>
        </div>
        {usarFiltro && (
          <div className="card" style={{ marginBottom: 16, padding: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
              <SelecaoMultipla label="Ramo" opcoes={RAMOS.map(v => ({ valor: v, label: v }))} valores={filtros.ramos} onChange={v => setFiltro('ramos', v)} />
              <SelecaoMultipla label="Motivo" opcoes={MOTIVOS_PERDA.map(v => ({ valor: v, label: v }))} valores={filtros.motivos} onChange={v => setFiltro('motivos', v)} />
              <SelecaoMultipla label="Status" opcoes={Object.entries(STATUS_RETORNO).map(([valor, s]) => ({ valor, label: s.label }))} valores={filtros.status} onChange={v => setFiltro('status', v)} />
              <div />
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Data do retorno de</label>
                <input className="form-input" type="date" value={filtros.retornoDe} onChange={e => setFiltro('retornoDe', e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">até</label>
                <input className="form-input" type="date" value={filtros.retornoAte} onChange={e => setFiltro('retornoAte', e.target.value)} />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" disabled={!temCriterio(filtros)} onClick={() => setFiltros(FILTROS_VAZIOS)}>Limpar filtros</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="page-content" style={{ paddingTop: 0 }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <span style={{ fontSize: 12, color: '#888' }}>{filtradas.length} {filtradas.length === 1 ? 'negociação' : 'negociações'}</span>
          </div>
          <div className="table-wrap">
            <table className="table-borda-destaque table-compacta">
              <thead>
                <tr>
                  {colunas.map(c => <SortableTh key={c.key} colKey={c.key} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>{c.label}</SortableTh>)}
                  <th>Obs</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginacao.itensPagina.map(c => {
                  const st = STATUS_RETORNO[statusRetorno(c)]
                  return (
                    <tr key={c.id}>
                      <td style={{ textAlign: 'center' }}>{fmtData(c.data_retorno)}</td>
                      <td style={{ fontWeight: 600 }}>{c.nome}</td>
                      <td style={{ textAlign: 'center' }}>{c.ramo || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{c.tipo || '—'}</td>
                      <td title={c.obs_perda || ''}>{c.motivo_perda || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{fmtData(c.perdido_em)}</td>
                      <td style={{ textAlign: 'center' }}><span className={`badge ${st.cls}`}>{st.label}</span></td>
                      <td>
                        <input className="form-input" style={{ minWidth: 160 }} placeholder="Observação..."
                          value={obsEdits[c.id] ?? c.obs_retorno ?? ''}
                          onChange={e => setObsEdits(prev => ({ ...prev, [c.id]: e.target.value }))}
                          onBlur={e => salvarObs(c.id, c.obs_retorno, e.target.value)} />
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => verCard(c)}>Ver</button>
                          <button type="button" className="btn btn-primary btn-sm" style={{ whiteSpace: 'nowrap' }}
                            title={c.retorno_funil_id ? 'Já existe um card de retorno — gera outro' : ''} onClick={() => gerarCard(c)}>Gerar Card</button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
                {!carregando && filtradas.length === 0 && (
                  <tr><td colSpan={colunas.length + 2} style={{ textAlign: 'center', color: '#aaa', padding: 32 }}>
                    {pesquisou ? 'Nenhuma negociação encontrada' : 'Use a busca ou o Filtro para ver as negociações perdidas.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Paginacao {...paginacao} rotulo="negociação" rotuloPlural="negociações" />
        </div>
      </div>
    </>
  )
}
